import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import { SubContractorStatusField } from "@/features/sub-contractors/SubContractorStatusField";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	formatPhoneNumber,
	handlePhoneInputChange,
	phoneValueForStorage,
} from "@/lib/dynamicForms/utils/phoneFormatter.util";
import {
	type SubContractorDisplay,
	type UpdateSubContractorInfo,
	organizationQueryOptions,
	useUpdateSubContractor,
} from "@/services/organization";
import { formatId } from "@/utils/format-id";
import { showApiErrorToast } from "@/utils/toast.utils";
import { isValidUSState, normalizeUSState } from "@/utils/us-states";
import { isValidUSZipcode } from "@/utils/us-zipcode";
import { SearchSchema } from "../$subConId";

const EditableField = {
	Name: "name",
	Street: "street1",
	City: "city",
	State: "state",
	PostalCode: "postalCode",
	Phone: "phone",
} as const;

type EditableField = (typeof EditableField)[keyof typeof EditableField];

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId/details",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function contactPayload(
	subContractor: SubContractorDisplay,
	patch: Partial<Record<EditableField, string>>,
): UpdateSubContractorInfo {
	const address = subContractor.address;
	const value = (field: EditableField, current: string | null | undefined) =>
		patch[field] !== undefined ? patch[field] || null : current || null;

	return {
		name: value(EditableField.Name, subContractor.name),
		phone: value(EditableField.Phone, subContractor.phone),
		address: {
			street1: value(EditableField.Street, address?.street1),
			street2: address?.street2 || null,
			city: value(EditableField.City, address?.city),
			state: value(EditableField.State, address?.state),
			country: address?.country || null,
			postalCode: value(EditableField.PostalCode, address?.postalCode),
		},
	};
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId/details",
	});
	const subContractorQuery = useQuery(
		organizationQueryOptions.subContractors.detail(
			params.orgId,
			params.subConId,
		),
	);
	const subContractor = subContractorQuery.data;
	const canUpdate = useEntityActionAllowed({
		name: "UpdateSubContractor",
		entityType: "SubContractor",
		entityId: params.subConId,
		enabled: params.subConId.length > 0,
	});
	const updateSubContractor = useUpdateSubContractor();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<EditableField>();
	const [editingField, setEditingField] = useState<EditableField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<EditableField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<EditableField, string>>
	>({});

	const showEdit = canUpdate.allowed;
	const labels: Record<EditableField, string> = {
		name: t("SubContractor.Name", { useFieldInfo: true }),
		street1: t("Address.Street1", { useFieldInfo: true }),
		city: t("Address.City", { useFieldInfo: true }),
		state: t("Address.State", { useFieldInfo: true }),
		postalCode: t("Address.PostalCode", { useFieldInfo: true }),
		phone: t("SubContractor.Phone", { useFieldInfo: true }),
	};

	const serverValue = (field: EditableField): string => {
		if (!subContractor) return "";
		if (field === EditableField.Name) return subContractor.name ?? "";
		if (field === EditableField.Phone) return subContractor.phone ?? "";
		if (field === EditableField.Street)
			return subContractor.address?.street1 ?? "";
		if (field === EditableField.City) return subContractor.address?.city ?? "";
		if (field === EditableField.State)
			return subContractor.address?.state ?? "";
		return subContractor.address?.postalCode ?? "";
	};

	const displayValue = (field: EditableField): string => {
		const value = serverValue(field);
		if (field === EditableField.Phone) return formatPhoneNumber(value);
		return value;
	};

	const beginEdit = (field: EditableField) => {
		setErrorByField((prev) => ({ ...prev, [field]: undefined }));
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: displayValue(field) }));
	};

	const cancelEdit = (field: EditableField) => {
		setErrorByField((prev) => ({ ...prev, [field]: undefined }));
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
		setEditingField((current) => (current === field ? null : current));
	};

	const commit = async (field: EditableField) => {
		const raw = draftByField[field];
		if (raw === undefined || !subContractor?.id) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}

		let next = raw.trim();
		if (field === EditableField.Phone) next = phoneValueForStorage(raw);
		if (field === EditableField.State && next)
			next = normalizeUSState(next) || next;

		if (next === serverValue(field)) {
			cancelEdit(field);
			return;
		}

		if (field === EditableField.Name && !next) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldRequired", { field: labels.name }),
			}));
			return;
		}
		if (field === EditableField.Name && next.length > 50) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.name,
					count: 50,
				}),
			}));
			return;
		}
		if (field === EditableField.Street && next.length > 100) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.street1,
					count: 100,
				}),
			}));
			return;
		}
		if (field === EditableField.City && next.length > 50) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.city,
					count: 50,
				}),
			}));
			return;
		}
		if (field === EditableField.State && next && !isValidUSState(next)) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.usStateInvalid"),
			}));
			return;
		}
		if (field === EditableField.PostalCode && next && !isValidUSZipcode(next)) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.zipcodeInvalid"),
			}));
			return;
		}
		if (field === EditableField.Phone && next && !/^\d{10}$/.test(next)) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.phoneOptionalTenDigits"),
			}));
			return;
		}

		setEditingField((current) => (current === field ? null : current));
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await updateSubContractor.mutateAsync({
				organizationId: params.orgId,
				subContractorId: subContractor.id,
				data: contactPayload(subContractor, { [field]: next }),
			});
			setErrorByField((prev) => ({ ...prev, [field]: undefined }));
			setDraftByField((prev) => {
				const cleared = { ...prev };
				delete cleared[field];
				return cleared;
			});
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			showApiErrorToast({
				error,
				message: t("org-admin.sub-contractors.details.saveFailed"),
			});
		}
	};

	const field = (key: EditableField) => (
		<DisplayInput
			key={key}
			label={labels[key]}
			value={displayValue(key)}
			state={
				editingField === key
					? DisplayFieldState.Active
					: errorByField[key]
						? DisplayFieldState.Error
						: DisplayFieldState.Enabled
			}
			showEdit={showEdit && editingField !== key}
			editAriaLabel={labels[key]}
			editValue={
				key === EditableField.Phone
					? (draftByField.phone ?? displayValue(key))
					: draftByField[key]
			}
			feedback={feedbackByField[key] ?? DisplayFieldFeedback.Idle}
			hintText={errorByField[key]}
			showHintText={Boolean(errorByField[key])}
			onEdit={() => beginEdit(key)}
			onEditChange={(value) =>
				setDraftByField((prev) => ({
					...prev,
					[key]:
						key === EditableField.Phone ? handlePhoneInputChange(value) : value,
				}))
			}
			onEditCommit={() => {
				void commit(key);
			}}
			onEditCancel={() => cancelEdit(key)}
			data-testid={`sub-contractor-${key}`}
		/>
	);

	return (
		<div className="flex w-full flex-col gap-(--spacing-xs,4px)">
			{field(EditableField.Name)}
			<SubContractorStatusField
				organizationId={params.orgId}
				subContractor={subContractor}
			/>
			{field(EditableField.Street)}
			{field(EditableField.City)}
			{field(EditableField.State)}
			{field(EditableField.PostalCode)}
			{field(EditableField.Phone)}
			<DisplayInput
				label={t("org-admin.sub-contractors.table.columns.userCount")}
				value={subContractor?.totalAssignedUserCount ?? 0}
				showEdit={false}
				data-testid="sub-contractor-user-count"
			/>
			<DisplayInput
				label={t("org-admin.sub-contractors.table.columns.id")}
				value={formatId(subContractor?.id)}
				copyable
				copyText={subContractor?.id ?? ""}
				showEdit={false}
				copyAriaLabel={t("common.actions.copy")}
				data-testid="sub-contractor-id"
			/>
		</div>
	);
}
